import { transformedUserInfo } from '../../assets/js/transform/user';
import { getProfileAPI, updateProfileAPI } from '../../services/api/profile.api';
import {
	GET_PROFILE_SUCCESS,
	GET_PROFILE_FAILURE,
	GET_PROFILE_REQUEST,
	UPDATE_PROFILE_SUCCESS,
	UPDATE_PROFILE_FAILURE,
	UPDATE_PROFILE_REQUEST,
	CLEAR_PROFILE,
} from '../types';
import { ROUTE_PERMISSION_KEY, ROUTE_PERMISSION_NAME } from '../../enums/permissions';
import { HTTP_STATUS_CODE } from '../../enums/http';

const flattenPermissions = (permissions) => {
	return permissions.reduce((acc, permission) => {
		let temp = [...acc];
		if (permission.key) {
			temp = [
				...temp,
				{
					key: permission.key,
					name: permission.name,
					permissions: permission.permissions,
				},
			];
		}
		if (permission.children) {
			temp = [
				...temp,
				...permission.children,
			];
		}
		return [...temp];
	}, []);
};

export default {
	namespaced: true,
	state: {
		profile: {
			isLoading: false,
			isUpdating: false,
			data: {},
		},
		permissions: [],
		isError500: false,
		isError401: false,
		isError403: false,
		isErrorNetwork: false,
		isErrorTwoFactor: false,
	},
	getters: {
		getProfile(state) {
			return state.profile.data;
		},
		getUsername(state) {
			return state.profile.data.staffId || state.profile.data.username;
		},
		// return route name for redirecting after login if have no any permissions, it will return forbidden page
		getDefaultRouteName(state) {
			const permission = state.permissions.find((val) => val.permissions.view) || [];
			return ROUTE_PERMISSION_NAME[permission.key] || ROUTE_PERMISSION_KEY.FORBIDDEN;
		},
		hasManageShipmentPermission(state) {
			const shipmentPermissionKey = ROUTE_PERMISSION_KEY.SHIPMENT;
			const shipmentPermission = state.permissions.find((permisison) => permisison.key === shipmentPermissionKey);

			return shipmentPermission && shipmentPermission.permissions.manage;
		},
	},
	mutations: {
		[GET_PROFILE_REQUEST](state) {
			state.profile = {
				...state.profile,
				isLoading: true,
			};
			state.isError500 = false;
			state.isError401 = false;
			state.isError403 = false;
			state.isErrorNetwork = false;
			state.isErrorTwoFactor = false;
		},
		[GET_PROFILE_SUCCESS](state, { data }) {
			state.profile = {
				...state.profile,
				isLoading: false,
				data: transformedUserInfo(data),
			};
			state.permissions = flattenPermissions(data?.role?.permissions || []);
		},
		[GET_PROFILE_FAILURE](state, error = {}) {
			const errorStatus = error?.response?.status || 0;
			const errorSlug = error?.response?.data?.error;
			// No response object means the request never reached the server
			// (offline, DNS failure, timeout). Track it so the router guard can
			// stop re-fetching and redirect to an error page instead of looping.
			const isNetworkError = !error?.response;

			state.profile = {
				...state.profile,
				isLoading: false,
			};
			state.permissions = [];
			state.isError401 = errorStatus === HTTP_STATUS_CODE.UNAUTHORIZED;
			state.isError403 = errorStatus === HTTP_STATUS_CODE.FORBIDDEN;
			state.isErrorTwoFactor = errorSlug === '2fa_required';
			state.isError500 = errorStatus === HTTP_STATUS_CODE.INTERNAL_SERVER_ERROR;
			state.isErrorNetwork = isNetworkError;
		},
		[UPDATE_PROFILE_REQUEST](state) {
			state.profile = {
				...state.profile,
				isUpdating: true,
			};
		},
		[UPDATE_PROFILE_SUCCESS](state, data) {
			state.profile = {
				...state.profile,
				isUpdating: false,
				data: transformedUserInfo(data.data),
			};
		},
		[UPDATE_PROFILE_FAILURE](state) {
			state.profile = {
				...state.profile,
				isUpdating: false,
			};
		},
		[CLEAR_PROFILE](state) {
			state.profile = {
				isLoading: false,
				isUpdating: false,
				data: {},
			};

			state.permissions = [];
		},
	},
	actions: {
		async getProfile({ commit }) {
			try {
				commit(GET_PROFILE_REQUEST);
				const { data } = await getProfileAPI();
				commit(GET_PROFILE_SUCCESS, data);
			} catch (error) {
				commit(GET_PROFILE_FAILURE, error);
			}
		},
		async updateProfile({ commit, dispatch }, params) {
			try {
				commit(UPDATE_PROFILE_REQUEST);
				const { data } = await updateProfileAPI(params);
				commit(UPDATE_PROFILE_SUCCESS, data);
				dispatch('toast/showUpdateSuccessToast', null, { root: true });
			} catch (error) {
				commit(UPDATE_PROFILE_FAILURE);
				dispatch('toast/showMultipleErrorsToast', error, { root: true });

				throw error;
			}
		},
		clearProfile({ commit }) {
			commit(CLEAR_PROFILE);
		},
	},
};
